import React, { useState } from 'react';
import { useAnalysisContext } from '../../../../context/AnalysisContext';
import { useResultsExport } from '../../../../hooks/useResultsExport';
import {
  Download,
  FileText,
  Table2,
  Share2,
  CheckCircle2,
  Copy,
  FileJson,
} from 'lucide-react';

/**
 * Export Tab - Export results in various formats
 *
 * Features:
 * - CSV export (table data)
 * - JSON export (complete results)
 * - Session export (full workflow state)
 * - Shareable URL generation
 */

export const ExportTab: React.FC = () => {
  const { analysisResult, selectedDisease, selectedAnalysisType } = useAnalysisContext();
  const {
    exportResultsTable,
    exportCompleteAnalysis,
    exportSession,
    copyShareURL,
  } = useResultsExport();

  const [urlCopied, setUrlCopied] = useState(false);

  if (!analysisResult) {
    return (
      <div className="p-8 text-center text-[var(--text-secondary)]">
        No results to export
      </div>
    );
  }

  // ========================================
  // HANDLERS
  // ========================================

  const handleCopyURL = async () => {
    const success = await copyShareURL();
    if (success) {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    }
  };

  // ========================================
  // MAIN RENDER
  // ========================================

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
          Export Results
        </h2>
        <p className="text-[var(--text-secondary)]">
          Download your analysis results in various formats
        </p>
      </div>

      {/* Export Options */}
      <div className="space-y-4">
        {/* CSV Export */}
        <div className="origin-card p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-[var(--success)]/10 flex items-center justify-center flex-shrink-0">
              <Table2 className="w-6 h-6 text-[var(--success)]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                Export as CSV
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Download results table with all lncRNAs and their metrics. Perfect for Excel or further statistical analysis.
              </p>
              <button
                onClick={exportResultsTable}
                className="origin-btn origin-btn-primary flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download CSV
              </button>
            </div>
          </div>
        </div>

        {/* JSON Export - Complete Analysis */}
        <div className="origin-card p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-[var(--origin-blue-500)]/10 flex items-center justify-center flex-shrink-0">
              <FileJson className="w-6 h-6 text-[var(--origin-blue-500)]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                Export Complete Analysis
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Download complete analysis with metadata, parameters, and all results in JSON format.
              </p>
              <button
                onClick={exportCompleteAnalysis}
                className="origin-btn origin-btn-secondary flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download JSON
              </button>
            </div>
          </div>
        </div>

        {/* Session Export */}
        <div className="origin-card p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-[var(--warning)]/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-[var(--warning)]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                Export Session
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Save your entire workflow state including disease selection, cell types, parameters, and results.
              </p>
              <button
                onClick={exportSession}
                className="origin-btn origin-btn-secondary flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download Session
              </button>
            </div>
          </div>
        </div>

        {/* Share URL */}
        <div className="origin-card p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-[var(--origin-blue-500)]/10 flex items-center justify-center flex-shrink-0">
              <Share2 className="w-6 h-6 text-[var(--origin-blue-500)]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                Share Analysis
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Generate a shareable URL with your analysis configuration (without results).
              </p>
              <button
                onClick={handleCopyURL}
                className={`origin-btn flex items-center gap-2 transition-all ${
                  urlCopied
                    ? 'origin-btn-success'
                    : 'origin-btn-secondary'
                }`}
              >
                {urlCopied ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    URL Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy Share URL
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Export Info */}
      <div className="origin-card p-6 bg-[var(--origin-blue-500)]/5 border-[var(--origin-blue-500)]">
        <h4 className="font-semibold text-[var(--text-primary)] mb-3">
          Export Information
        </h4>
        <div className="space-y-2 text-sm text-[var(--text-secondary)]">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[var(--origin-blue-500)]" />
            <span>
              <strong className="text-[var(--text-primary)]">Disease:</strong> {selectedDisease}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[var(--origin-blue-500)]" />
            <span>
              <strong className="text-[var(--text-primary)]">Analysis Type:</strong>{' '}
              {selectedAnalysisType.charAt(0).toUpperCase() + selectedAnalysisType.slice(1)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[var(--origin-blue-500)]" />
            <span>
              <strong className="text-[var(--text-primary)]">Results:</strong>{' '}
              {analysisResult.lncrna_results?.length || 0} lncRNAs identified
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[var(--origin-blue-500)]" />
            <span>
              <strong className="text-[var(--text-primary)]">Timestamp:</strong>{' '}
              {new Date(analysisResult.timestamp).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Citation */}
      <div className="origin-card p-6">
        <h4 className="font-semibold text-[var(--text-primary)] mb-3">
          Citation
        </h4>
        <div className="p-4 bg-[var(--bg-secondary)] rounded-lg font-mono text-sm text-[var(--text-secondary)]">
          lncRNA Target Discovery Platform. Analysis performed on{' '}
          {new Date(analysisResult.timestamp).toLocaleDateString()}.
          Disease: {selectedDisease}. Analysis type: {selectedAnalysisType}.
        </div>
      </div>
    </div>
  );
};

export default ExportTab;
